import type {
  AnalysisRecipeSearchSource,
  AnalysisSheetRecipe,
} from '@/types/analysisRecipe.types';
import type { SearchFilterDraft } from '@/helpers/searchFilters.helpers';
import {
  describeToolFilter,
  toToolFilters,
  type SearchPropertyFilter,
} from '@/helpers/searchFilters.helpers';

/**
 * Its own id space, so it never displaces the label's ordinary sheet.
 *
 * Keyed on everything that changes which rows the sheet holds. Keying on the
 * query alone made "acme" and "acme filtered to open tickets" the same sheet
 * under the same name, so one silently replaced the other.
 */
export const searchSheetId = (
  label: string,
  query: string,
  variant = '',
): string =>
  `analysis-search:${encodeURIComponent(label)}:${encodeURIComponent(query)}` +
  (variant ? `:${variant}` : '');

/**
 * A short, stable tag for the options that are not the query.
 *
 * `mode` counts: a meaning search and a keyword search over the same label,
 * query and filters return different rows, so they are different sheets. It is
 * folded in only when it is NOT the default, so every keyword sheet keeps the
 * id it already had — including a semantic one, once Meaning ships, in a
 * project whose keyword sheets were saved before it.
 */
function searchVariantTag(
  mode: AnalysisRecipeSearchSource['mode'],
  contains: boolean,
  filters: SearchPropertyFilter[],
): string {
  const isKeyword = mode === 'keyword';
  if (isKeyword && !contains && filters.length === 0) return '';
  const source = JSON.stringify(
    isKeyword ? { contains, filters } : { mode, contains, filters },
  );
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 31 + source.charCodeAt(index)) | 0;
  }
  return Math.abs(hash).toString(36);
}

/**
 * Everything about a search that is not the query, in the reader's words.
 *
 * The id already keys on these — two searches differing only by a filter are
 * two sheets. What they say about themselves has to differ too, or the reader
 * ends up with two tabs called the same thing holding different rows.
 */
export function searchConditionSummary(search: {
  contains: boolean;
  filters?: ReadonlyArray<Record<string, unknown>>;
}): string[] {
  return [
    ...(search.contains ? ['fragments'] : []),
    ...(search.filters ?? []).map(describeToolFilter),
  ];
}

/** How many conditions a tab name spells out before it summarises the rest. */
const NAMED_CONDITIONS_IN_TAB = 2;

/**
 * How a search-sourced sheet names itself in the tab strip.
 *
 * The conditions are part of the name, not decoration: without them a filtered
 * search and an unfiltered one are two tabs reading `Identity · db2` side by
 * side. Only the first couple are spelled out — a tab is narrow, and a name
 * that truncates before its distinguishing part is no better than none.
 */
export const searchSheetName = (
  label: string,
  query: string,
  conditions: readonly string[] = [],
): string => {
  const base = `${label} · ${query}`;
  if (conditions.length === 0) return base;
  const named = conditions.slice(0, NAMED_CONDITIONS_IN_TAB);
  const rest = conditions.length - named.length;
  return `${base} · ${named.join(', ')}${rest > 0 ? ` +${rest} more` : ''}`;
};

export interface BuildSearchSheetInput {
  label: string;
  query: string;
  mode: AnalysisRecipeSearchSource['mode'];
  contains: boolean;
  filters: SearchFilterDraft[];
  /** The label's ordinary sheet, so the new one joins its family. */
  rootSheetId: string;
}

/**
 * A sheet whose rows are a search's hits.
 *
 * Carries `worksetRootSheetId` so it behaves like a gathered sheet: its own
 * view-state slot, closable, and scoped to the family it was opened from. That
 * matters because it shows a slice of the label rather than the label, and
 * sharing the root's slot would have made the two overwrite each other's
 * filters and sort.
 */
export function buildSearchSheet(
  input: BuildSearchSheetInput,
): AnalysisSheetRecipe {
  const query = input.query.trim();
  const filters = toToolFilters(input.filters);
  const variant = searchVariantTag(input.mode, input.contains, filters);
  const search: AnalysisRecipeSearchSource = {
    query,
    mode: input.mode,
    contains: input.contains,
    // Only recorded when there are any: an empty array would serialise as a
    // meaningless difference between two otherwise identical sheets.
    ...(filters.length
      ? { filters: filters as AnalysisRecipeSearchSource['filters'] }
      : {}),
  };
  return {
    id: searchSheetId(input.label, query, variant),
    name: searchSheetName(
      input.label,
      query,
      searchConditionSummary({ contains: input.contains, filters }),
    ),
    face: 'analysis',
    source: { kind: 'label', label: input.label, search },
    worksetRootSheetId: input.rootSheetId,
    steps: [],
    keepFilter: null,
    controls: [],
    breakdown: null,
    charts: [],
  };
}
